import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Star, Heart, Truck, Package, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/lib/currency";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [mainImg, setMainImg] = useState(0);
  const [zoom, setZoom] = useState({ on: false, x: 50, y: 50 });
  const [reviews, setReviews] = useState([]);
  const [related, setRelated] = useState([]);
  const [tab, setTab] = useState("description");
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const { add } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    setP(null); setMainImg(0); setSize(null); setColor(null); setQty(1);
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setSize(r.data.sizes?.[0] || null);
      setColor(r.data.colors?.[0] || null);
    });
    api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data));
    api.get(`/ai/recommendations/${id}`).then((r) => setRelated(r.data));
  }, [id]);

  if (!p) return <div className="text-center py-32 overline">Loading…</div>;

  const handleAdd = () => {
    add(p, { quantity: qty, size, color });
    toast.success(`${p.name} added to your selection`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.message("Sign in to leave a review."); return; }
    try {
      await api.post("/reviews", { product_id: id, rating: reviewRating, comment: reviewText });
      const r = await api.get(`/products/${id}/reviews`);
      setReviews(r.data); setReviewText("");
      toast.success("Thank you — your review is published.");
    } catch { toast.error("Couldn't post review."); }
  };

  return (
    <div data-testid={`product-detail-${p.id}`} className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-10 pb-20">
      <div className="overline mb-8">
        <Link to="/shop">Shop</Link> / <Link to={`/shop/${p.category}`}>{p.category}</Link> / <span className="text-[#0A0A0A]">{p.name}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div className="flex gap-4">
          <div className="hidden lg:flex flex-col gap-3">
            {p.images.map((src, i) => (
              <button key={i} onClick={() => setMainImg(i)} className={`w-20 aspect-portrait border ${i === mainImg ? "border-[#0A0A0A]" : "border-transparent"}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div
            className="flex-1 aspect-portrait bg-[#EFEFE8] overflow-hidden relative cursor-zoom-in"
            onMouseMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setZoom({ on: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
            }}
            onMouseLeave={() => setZoom((z) => ({ ...z, on: false }))}
          >
            <img
              src={p.images[mainImg]}
              alt={p.name}
              className="w-full h-full object-cover transition-transform duration-300"
              style={zoom.on ? { transform: `scale(1.9)`, transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}
            />
          </div>
        </div>

        {/* Info */}
        <div className="lg:pl-8">
          <div className="overline">{p.category}</div>
          <h1 className="serif text-4xl lg:text-5xl mt-2 leading-tight">{p.name}</h1>
          <div className="flex items-center gap-2 mt-3 text-xs text-[#5A5A5A]">
            <div className="flex gap-0.5 text-[#C5A059]">
              {Array.from({length: 5}).map((_, i) => <Star key={i} size={12} fill={i < Math.round(p.rating) ? "#C5A059" : "none"} strokeWidth={1.2} />)}
            </div>
            <span>{p.rating} · {reviews.length} reviews</span>
          </div>
          <div className="serif text-3xl mt-6">{formatPrice(p.price, { decimals: 0 })}</div>

          <p className="mt-6 text-[#5A5A5A] leading-relaxed">{p.description}</p>

          {/* Colors */}
          {p.colors?.length > 0 && (
            <div className="mt-8">
              <div className="fld-label mb-3">Color · <span className="text-[#0A0A0A] normal-case tracking-normal">{color}</span></div>
              <div className="flex gap-2">
                {p.colors.map((c) => (
                  <button
                    key={c} onClick={() => setColor(c)}
                    data-testid={`color-option-${c}`}
                    className={`px-4 py-2 text-xs border ${color === c ? "border-[#0A0A0A]" : "border-[#E5E5DF]"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sizes */}
          {p.sizes?.length > 0 && (
            <div className="mt-6">
              <div className="fld-label mb-3">Size</div>
              <div className="flex gap-2 flex-wrap">
                {p.sizes.map((s) => (
                  <button
                    key={s} onClick={() => setSize(s)}
                    data-testid={`size-option-${s}`}
                    className={`px-4 py-2 min-w-12 text-xs border ${size === s ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5DF]"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Qty + add */}
          <div className="mt-10 flex gap-3">
            <div className="inline-flex items-center border border-[#0A0A0A]">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-3">−</button>
              <span className="px-4">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-4 py-3">+</button>
            </div>
            <button data-testid="add-to-cart-button" onClick={handleAdd} className="btn-primary flex-1 justify-center">Add to bag</button>
          </div>

          <button
            data-testid="wishlist-button"
            onClick={async () => {
              if (!user) { toast.message("Sign in to save."); return; }
              await api.post(`/wishlist/${p.id}`); toast.success("Saved to wishlist");
            }}
            className="flex items-center gap-2 mt-4 text-xs uppercase tracking-[0.25em] hover:text-[#C5A059]"
          >
            <Heart size={14} /> Save to wishlist
          </button>

          <div className="mt-10 grid grid-cols-3 gap-4 text-xs text-[#5A5A5A]">
            <div className="flex items-start gap-2"><Truck size={16} strokeWidth={1.2} /><span>Complimentary shipping over AED 200</span></div>
            <div className="flex items-start gap-2"><Package size={16} strokeWidth={1.2} /><span>30-day returns</span></div>
            <div className="flex items-start gap-2"><ShieldCheck size={16} strokeWidth={1.2} /><span>Secure checkout</span></div>
          </div>

          <div className="mt-10 border-t border-[#E5E5DF] pt-6 text-sm">
            <div className="fld-label mb-2">Material</div>
            <div>{p.material || "—"}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-24 border-t border-[#E5E5DF] pt-10">
        <div className="flex gap-8 mb-10">
          {["description", "shipping", "reviews"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`text-[0.7rem] tracking-[0.25em] uppercase pb-2 border-b ${tab === t ? "border-[#C5A059] text-[#0A0A0A]" : "border-transparent text-[#5A5A5A]"}`}>
              {t === "reviews" ? `Reviews (${reviews.length})` : t}
            </button>
          ))}
        </div>
        {tab === "description" && <p className="max-w-2xl text-[#5A5A5A] leading-relaxed">{p.description}</p>}
        {tab === "shipping" && (
          <div className="max-w-2xl text-[#5A5A5A] space-y-3">
            <p>Complimentary worldwide shipping on orders above $200. Orders ship within 2 business days from our Paris atelier via DHL Express.</p>
            <p>30-day return window for unworn pieces with original packaging. Custom items are final sale.</p>
          </div>
        )}
        {tab === "reviews" && (
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              {reviews.length === 0 && <p className="text-[#5A5A5A]">No reviews yet — be the first to share your thoughts.</p>}
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-[#E5E5DF] pb-5">
                  <div className="flex gap-0.5 text-[#C5A059]">
                    {Array.from({length: 5}).map((_, i) => <Star key={i} size={11} fill={i < r.rating ? "#C5A059" : "none"} strokeWidth={1.2} />)}
                  </div>
                  <p className="mt-2 text-sm">{r.comment}</p>
                  <div className="overline mt-2">{r.user_name}</div>
                </div>
              ))}
            </div>
            <form onSubmit={submitReview} className="border-l border-[#E5E5DF] lg:pl-10">
              <div className="serif text-2xl">Share your impression</div>
              <div className="mt-5">
                <div className="fld-label">Rating</div>
                <div className="flex gap-1 mt-2">
                  {[1,2,3,4,5].map((n) => (
                    <button type="button" key={n} onClick={() => setReviewRating(n)}>
                      <Star size={20} fill={n <= reviewRating ? "#C5A059" : "none"} strokeWidth={1.2} className="text-[#C5A059]" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-5">
                <div className="fld-label">Comment</div>
                <textarea data-testid="review-comment" value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={4} className="fld mt-2 resize-none" required />
              </div>
              <button data-testid="submit-review" className="btn-primary mt-6">Publish</button>
            </form>
          </div>
        )}
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="mt-24">
          <div className="overline">You may also love</div>
          <h3 className="serif text-3xl lg:text-4xl mt-3 mb-10">Curated for this piece.</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12">
            {related.map((rp, i) => <ProductCard key={rp.id} product={rp} idx={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
